import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import type { ComponentPropsWithoutRef, ReactNode } from 'react';
import { cn } from '../../lib/utils';

/**
 * Right-side sheet (480px), used for "list + siderude" (direction A): the list stays visible
 * behind a light overlay while the sheet slides in from the right. Built on Radix Dialog so
 * Esc/✕, focus trap and focus-return to the trigger come for free — only the positioning and
 * slide-in animation differ from `ui/Dialog.tsx`'s centered modal.
 */
export const Sheet = DialogPrimitive.Root;
export const SheetTrigger = DialogPrimitive.Trigger;

export function SheetContent({
  className,
  children,
  title,
  eyebrow,
  description,
  ...props
}: ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
  title: ReactNode;
  eyebrow?: string;
  description: ReactNode;
}) {
  return (
    <DialogPrimitive.Portal>
      <DialogPrimitive.Overlay
        className={cn(
          'fixed inset-0 z-40 bg-black/20',
          'data-[state=open]:opacity-100 data-[state=closed]:opacity-0 transition-opacity duration-150',
        )}
      />
      <DialogPrimitive.Content
        className={cn(
          'fixed right-0 top-0 bottom-0 z-50 w-[480px] max-w-[92vw]',
          'bg-card border-l border-border shadow-2xl',
          'flex flex-col outline-none',
          'transition-transform duration-200 ease-out',
          'data-[state=open]:translate-x-0 data-[state=closed]:translate-x-full',
          className,
        )}
        {...props}
      >
        <div className="flex items-start gap-3 px-5 py-4 border-b border-border flex-shrink-0">
          <div className="min-w-0 flex-grow">
            {eyebrow ? (
              <div className="text-[11px] uppercase tracking-wide text-muted font-semibold">
                {eyebrow}
              </div>
            ) : null}
            <DialogPrimitive.Title className="text-[17px] font-semibold truncate">
              {title}
            </DialogPrimitive.Title>
            <DialogPrimitive.Description className="text-sm text-muted mt-1">
              {description}
            </DialogPrimitive.Description>
          </div>
          <DialogPrimitive.Close
            aria-label="Close"
            className="flex-shrink-0 h-9 w-9 flex items-center justify-center rounded-md hover:bg-bg"
          >
            <X size={16} aria-hidden="true" />
          </DialogPrimitive.Close>
        </div>
        {children}
      </DialogPrimitive.Content>
    </DialogPrimitive.Portal>
  );
}

/** Scrollable body of the sheet — pairs with `SheetFooter`, which stays pinned below it. */
export function SheetBody({ className, children }: { className?: string; children: ReactNode }) {
  return (
    <div
      className={cn('flex-grow min-h-0 overflow-y-auto px-5 py-5 flex flex-col gap-5', className)}
    >
      {children}
    </div>
  );
}

export function SheetFooter({ children }: { children: ReactNode }) {
  return (
    <div className="flex-shrink-0 flex flex-wrap justify-end gap-2 px-5 py-3.5 border-t border-border">
      {children}
    </div>
  );
}

export function SheetClose({ children }: { children: ReactNode }) {
  return <DialogPrimitive.Close asChild>{children}</DialogPrimitive.Close>;
}
